import { Injectable, InjectionToken, Optional, Inject } from '@angular/core';
// Ready to drop into an Angular project. Pure, testable functions + optional service wrapper.

/*
  Notes:
  - Matches the behavior of the original C++ code: inputs are a Calendar-like date (year counted from 2000),
    a GMT offset in MINUTES, latitude/longitude as DMS with sign, and optional minute corrections.
  - Returns sunrise/sunset in minutes from local midnight (0–1439), or null when out of bounds / polar day/night.
  - Precision: on par with Meeus-based implementations (sub‑minute at mid‑latitudes, given ideal conditions).
*/

// -------------------------------------------------------------
// Types
// -------------------------------------------------------------
export interface OrbisCalendar {
  hour: number;   // not used by the algorithm, kept for parity
  minute: number; // not used by the algorithm, kept for parity
  second: number; // not used by the algorithm, kept for parity
  weekday: number; // 0–6 (optional / unused)
  month: number;   // 1–12
  date: number;    // 1–31
  year: number;    // years since 2000 (e.g., 23 => 2023)
  _NAN?: number;
}

export interface DmsCoord {
  grados: number;   // can be negative; if so, signo should be -1
  minutos: number;  // 0–59
  segundos: number; // 0–59
  signo: 1 | -1 | 0; // 1 positive, -1 negative, 0 auto by grados sign
}

export interface SunTimesResult {
  sunrise: number | null; // minutes from local midnight [0..1439]
  sunset: number | null;  // minutes from local midnight [0..1439]
  isAllDay: boolean;      // midnight sun (never sets)
  isAllNight: boolean;    // polar night (never rises)
}

// -------------------------------------------------------------
// Constants & helpers
// -------------------------------------------------------------
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
const ZONE_MAX_SUNTIME_LIMIT = 1439; // [0..1439]

/** Normalize an angle in degrees to [0, 360). */
function norm360(deg: number): number {
  let x = deg % 360;
  if (x < 0) x += 360;
  return x;
}

/** Convert DMS + sign to decimal degrees. */
export function dmsToDecimal(coord: DmsCoord): number {
  const abs = Math.abs(coord.grados) + coord.minutos / 60 + coord.segundos / 3600;
  const sign = coord.signo !== 0 ? coord.signo : (coord.grados < 0 ? -1 : 1);
  return sign < 0 ? -abs : abs;
}

/** Convenience: build a DMS object from a decimal degree value. */
export function decimalToDms(decimal: number): DmsCoord {
  const sign: 1 | -1 = decimal < 0 ? -1 : 1;
  const abs = Math.abs(decimal);
  const grados = Math.trunc(abs) * sign; // keep sign on grados for parity
  const rem = (abs - Math.trunc(abs)) * 60;
  const minutos = Math.trunc(rem);
  const segundos = Math.round((rem - minutos) * 60);
  return { grados, minutos, segundos, signo: sign };
}

/** Leap year check for full Gregorian year. */
function isLeap(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

/** Normalize minutes to [0, 1439]. */
function normMinutes(m: number): number {
  while (m < 0) m += 1440;
  while (m >= 1440) m -= 1440;
  return m;
}

// -------------------------------------------------------------
// Core algorithm (Meeus) — faithful to the C++ implementation
// -------------------------------------------------------------
export function calculateSunTimes(
  cal: OrbisCalendar,
  gmtMinutes: number, // timezone offset in minutes (e.g., 120 for UTC+2)
  lat: DmsCoord,
  lng: DmsCoord,
  corrSunrise = 0, // extra correction in minutes
  corrSunset = 0   // extra correction in minutes
): SunTimesResult {
  let isAllDay = false;
  let isAllNight = false;

  // 1) Convert coordinates to decimal degrees
  const longitude = dmsToDecimal(lng);
  const latitude = dmsToDecimal(lat);
  const latRad = latitude * DEG2RAD;

  // 2) Day of year (N)
  const fullYear = 2000 + cal.year;
  const month = cal.month;
  const day = cal.date;
  const dim = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let N = day;
  for (let m = 1; m < month; m++) {
    N += dim[m];
    if (m === 2 && isLeap(fullYear)) N += 1;
  }

  // 3) Julian Day at 12:00 UTC, then Julian centuries from J2000.0
  let Y = fullYear;
  let M = month;
  let D = day;
  if (M <= 2) { Y -= 1; M += 12; }
  const A = Math.trunc(Y / 100);
  const B = 2 - A + Math.trunc(A / 4);
  const JD = Math.floor(365.25 * (Y + 4716)) + Math.floor(30.6001 * (M + 1)) + D + B - 1524.5;
  const T = (JD - 2451545.0) / 36525.0;

  // 4) Mean longitude of the Sun (L0)
  let L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T;
  L0 = norm360(L0);

  // 5) Mean anomaly of the Sun (M)
  let M_s = 357.52911 + 35999.05029 * T - 0.0001537 * T * T;
  M_s = norm360(M_s);
  const M_rad = M_s * DEG2RAD;

  // 6) Equation of center
  const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M_rad)
    + (0.019993 - 0.000101 * T) * Math.sin(2 * M_rad)
    + 0.000289 * Math.sin(3 * M_rad);

  // 7) True longitude of the Sun
  const sunTrueLong = L0 + C;

  // 8) Apparent longitude
  const omega = 125.04 - 1934.136 * T;
  const lambda = sunTrueLong - 0.00569 - 0.00478 * Math.sin(omega * DEG2RAD);

  // 9) Obliquity of the ecliptic (mean + correction)
  const epsilon0 = 23 + 26 / 60 + 21.448 / 3600
    - (46.8150 * T + 0.00059 * T * T - 0.001813 * T * T * T) / 3600;
  const epsilon = epsilon0 + 0.00256 * Math.cos(omega * DEG2RAD);
  const epsilonRad = epsilon * DEG2RAD;

  // 10) Solar declination
  const declinationRad = Math.asin(Math.sin(epsilonRad) * Math.sin(lambda * DEG2RAD));

  // 11) Equation of time (minutes)
  const e = 0.016708634 - 0.000042037 * T - 0.0000001267 * T * T; // eccentricity
  const y = Math.tan(epsilonRad / 2);
  const y2 = y * y;
  const EoT_rad = y2 * Math.sin(2 * L0 * DEG2RAD)
    - 2 * e * Math.sin(M_rad)
    + 4 * e * y2 * Math.sin(M_rad) * Math.cos(2 * L0 * DEG2RAD)
    - 0.5 * y2 * y2 * Math.sin(4 * L0 * DEG2RAD)
    - 1.25 * e * e * Math.sin(2 * M_rad);
  const equationOfTime = EoT_rad * RAD2DEG * 4; // minutes per degree

  // 12) Hour angle at horizon (with refraction & solar semidiameter = -0.833°)
  const horizonAngle = -0.833 * DEG2RAD;
  const cosH0 = (Math.sin(horizonAngle) - Math.sin(latRad) * Math.sin(declinationRad)) /
    (Math.cos(latRad) * Math.cos(declinationRad));

  if (cosH0 > 1) {
    // Sun never rises
    return { sunrise: null, sunset: null, isAllDay: false, isAllNight: true };
  }
  if (cosH0 < -1) {
    // Sun never sets
    return { sunrise: null, sunset: null, isAllDay: true, isAllNight: false };
  }

  const H0 = Math.acos(cosH0) * RAD2DEG; // in degrees

  // 13) True solar time for sunrise/sunset (hours)
  const solarSunrise = 12 - (H0 / 15) - (equationOfTime / 60);
  const solarSunset = 12 + (H0 / 15) - (equationOfTime / 60);

  // 14) Convert by longitude to UTC-based local solar time, then add timezone
  const lonTimeOffset = longitude / 15; // hours
  let localSunriseHours = solarSunrise - lonTimeOffset;
  let localSunsetHours = solarSunset - lonTimeOffset;

  // Normalize hours to [0, 24)
  while (localSunriseHours < 0) localSunriseHours += 24;
  while (localSunriseHours >= 24) localSunriseHours -= 24;
  while (localSunsetHours < 0) localSunsetHours += 24;
  while (localSunsetHours >= 24) localSunsetHours -= 24;

  // 15) Convert to minutes, add GMT and user corrections
  let sunriseMin = Math.round(localSunriseHours * 60);
  let sunsetMin = Math.round(localSunsetHours * 60);

  sunriseMin += gmtMinutes + corrSunrise;
  sunsetMin += gmtMinutes + corrSunset;

  sunriseMin = normMinutes(sunriseMin);
  sunsetMin = normMinutes(sunsetMin);

  // Bounds check like original code
  const sunrise = (sunriseMin < 0 || sunriseMin > ZONE_MAX_SUNTIME_LIMIT) ? null : sunriseMin;
  const sunset = (sunsetMin < 0 || sunsetMin > ZONE_MAX_SUNTIME_LIMIT) ? null : sunsetMin;

  return { sunrise, sunset, isAllDay, isAllNight };
}

// -------------------------------------------------------------
// Pretty helpers
// -------------------------------------------------------------
export function minutesToHHMM(m: number | null): string {
  if (m == null) return "--:--";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// -------------------------------------------------------------
// Optional Angular service wrapper (Angular @Injectable)
// -------------------------------------------------------------
// Usage:
//   - Provide a default timezone offset minutes via the SUN_TIMES_TZ_OFFSET token (optional).
//   - Or omit it and we'll use the runtime local offset: -new Date().getTimezoneOffset().

export interface SunTimesWithStrings extends SunTimesResult {
  sunriseStr: string;
  sunsetStr: string;
}

// Injection token to optionally configure a default timezone offset (in minutes, e.g., 120 for UTC+2)
export const SUN_TIMES_TZ_OFFSET = new InjectionToken<number>('SUN_TIMES_TZ_OFFSET');

@Injectable({ providedIn: 'root' })
export class AstCalendarService {
  constructor(
    @Optional() @Inject(SUN_TIMES_TZ_OFFSET) private readonly defaultTzOffsetMinutes?: number
  ) { }

  /** Core: same as before, but keeps the nice formatted strings */
  getSunTimes(
    cal: OrbisCalendar,
    tzOffsetMinutes: number,
    lat: DmsCoord,
    lng: DmsCoord,
    corrSunrise = 0,
    corrSunset = 0
  ): SunTimesWithStrings {
    const res = calculateSunTimes(cal, tzOffsetMinutes, lat, lng, corrSunrise, corrSunset);
    return {
      ...res,
      sunriseStr: minutesToHHMM(res.sunrise),
      sunsetStr: minutesToHHMM(res.sunset),
    };
  }

  /** Convenience: pass JS primitives (Date + decimal lat/lng). Uses configured tz or system tz. */
  getSunTimesForDate(
    date: Date,
    latDecimal: number,
    lngDecimal: number,
    corrSunrise = 0,
    corrSunset = 0
  ): SunTimesWithStrings {
    const cal = calendarFromDate(date);
    const lat = decimalToDms(latDecimal);
    const lng = decimalToDms(lngDecimal);
    const tz = this.defaultTzOffsetMinutes ?? -date.getTimezoneOffset();
    return this.getSunTimes(cal, tz, lat, lng, corrSunrise, corrSunset);
  }

  getSunTimesForWeek(
    startDate: Date,
    latDecimal: number,
    lngDecimal: number,
    length = 7,
    locale = 'es-ES',
    corrSunrise = 0,
    corrSunset = 0
  ): SunDayRow[] {
    return buildWeekWindow(
      this,
      startDate,
      latDecimal,
      lngDecimal,
      length,
      locale,
      corrSunrise,
      corrSunset
    );
  }
}



// -------------------------------------------------------------
// Small convenience: build OrbisCalendar from a JS Date and a desired local zone offset.
// Note: original structure encodes year as (fullYear - 2000).
// -------------------------------------------------------------
export function calendarFromDate(date: Date): OrbisCalendar {
  return {
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    weekday: date.getDay(),
    month: date.getMonth() + 1,
    date: date.getDate(),
    year: date.getFullYear() - 2000,
    _NAN: 0,
  };
}

// -------------------------------------------------------------
// Example usage (uncomment to test in a non-Angular TS runtime):
// -------------------------------------------------------------
// const cal = calendarFromDate(new Date("2025-07-15T00:00:00Z"));
// const lat = decimalToDms(40.4168);   // Madrid
// const lng = decimalToDms(-3.7038);
// const tzOffsetMinutes = 120; // CEST UTC+2 in summer
// const out = calculateSunTimes(cal, tzOffsetMinutes, lat, lng);
// console.log(out, minutesToHHMM(out.sunrise), minutesToHHMM(out.sunset));

// -------------------------------------------------------------
// Week window helpers (today -> next 6 days) and generic window from any date
// -------------------------------------------------------------
export interface SunDayRow extends SunTimesWithStrings {
  date: Date;
  weekday: string; // localized
}

/** Localized weekday name */
export function weekdayName(date: Date, locale = 'es-ES'): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
}

/**
 * Get a 7-day window starting from the provided date (default length = 7).
 * Computes sunrise/sunset for each day using decimal lat/lng.
 */
export function buildWeekWindow(
  svc: AstCalendarService,
  startDate: Date,
  latDecimal: number,
  lngDecimal: number,
  length = 7,
  locale = 'es-ES',
  corrSunrise = 0,
  corrSunset = 0
): SunDayRow[] {
  const out: SunDayRow[] = [];
  for (let i = 0; i < length; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    const r = svc.getSunTimesForDate(d, latDecimal, lngDecimal, corrSunrise, corrSunset);
    out.push({ ...r, date: d, weekday: weekdayName(d, locale) });
  }
  return out;
}

/**
 * Convenience: 7-day window starting today (local system date/timezone by default).
 */
export function buildWeekWindowFromToday(
  svc: AstCalendarService,
  latDecimal: number,
  lngDecimal: number,
  locale = 'es-ES',
  corrSunrise = 0,
  corrSunset = 0
): SunDayRow[] {
  return buildWeekWindow(svc, new Date(), latDecimal, lngDecimal, 7, locale, corrSunrise, corrSunset);
}

// -------------------------------------------------------------
// Example Angular table usage (standalone snippet)
// -------------------------------------------------------------
/*
import { Component, inject, signal } from '@angular/core';
import { AstCalendarService, buildWeekWindowFromToday, SunDayRow } from './suntimes';

@Component({
  selector: 'app-sun-week',
  standalone: true,
  template: `
    <table>
      <thead>
        <tr>
          <th>Día</th>
          <th>Fecha</th>
          <th>Amanecer</th>
          <th>Atardecer</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of rows">
          <td>{{ row.weekday }}</td>
          <td>{{ row.date | date:'dd/MM/yyyy' }}</td>
          <td>{{ row.sunriseStr }}</td>
          <td>{{ row.sunsetStr }}</td>
        </tr>
      </tbody>
    </table>
  `
})
export class SunWeekComponent {
  private svc = inject(AstCalendarService);
  rows: SunDayRow[] = [];

  ngOnInit() {
    const lat = 40.4168; // Madrid
    const lng = -3.7038;
    this.rows = buildWeekWindowFromToday(this.svc, lat, lng);
  }
}
*/

